import { delay } from "../promise";
import type { RetryOptions } from "./types";
import { RetryExhaustedError } from "./types";

export * from "./reconnect-delay";
export * from "./types";

/**
 * Execute an async function with exponential backoff retry logic
 *
 * @param fn - The async function to execute
 * @param options - Retry configuration options
 * @returns The result of the async function if successful
 * @throws RetryExhaustedError if all retry attempts are exhausted
 * @throws Error if shouldRetry returns false for a given error
 *
 * @example
 * ```typescript
 * // Basic usage with defaults (2 retries, 1s base delay)
 * const result = await retryWithBackoff(async () => {
 *   return await fetchData();
 * });
 *
 * // Custom retry logic for HTTP 429 (rate limiting)
 * const result = await retryWithBackoff(
 *   async () => await httpClient.post(url, data),
 *   {
 *     maxAttempts: 2,
 *     baseDelay: 1000,
 *     shouldRetry: (error) => {
 *       return error instanceof AxiosError && error.response?.status === 429;
 *     },
 *     getRetryDelay: (error) => {
 *       if (error instanceof AxiosError && error.response?.headers['retry-after']) {
 *         return parseInt(error.response.headers['retry-after'], 10) * 1000;
 *       }
 *       return undefined; // Use exponential backoff
 *     },
 *     onRetry: (error, attempt, delay) => {
 *       console.log(`Retrying after ${delay}ms (attempt ${attempt})`);
 *     },
 *   }
 * );
 * ```
 */
export async function retryWithBackoff<T>(fn: () => Promise<T>, options: RetryOptions = {}): Promise<T> {
  const { maxAttempts = 2, baseDelay = 1000, shouldRetry = () => true, getRetryDelay, onRetry } = options;

  let lastError: unknown;

  // Initial attempt + retries
  for (let attempt = 0; attempt <= maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      // Check if we should retry this error
      if (!shouldRetry(error, attempt)) {
        throw error;
      }

      // Don't retry on the last attempt
      if (attempt === maxAttempts) {
        throw new RetryExhaustedError(
          `All retry attempts exhausted after ${maxAttempts + 1} tries`,
          error,
          maxAttempts + 1,
        );
      }

      // Calculate delay: use custom delay if provided, otherwise exponential backoff
      const customDelay = getRetryDelay?.(error, attempt);
      const retryDelay = customDelay !== undefined ? customDelay : baseDelay * 2 ** attempt;

      // Invoke retry callback if provided
      onRetry?.(error, attempt + 1, retryDelay);

      // Wait before retrying
      await delay(undefined, retryDelay);
    }
  }

  // Should never reach here, but TypeScript needs this
  throw new RetryExhaustedError(
    `All retry attempts exhausted after ${maxAttempts + 1} tries`,
    lastError,
    maxAttempts + 1,
  );
}
